import React, { useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';
import mixpanel from 'mixpanel-browser';
import _debounce from 'lodash/debounce';

import {
  InputAdornment,
  Typography,
  IconButton,
  LinearProgress,
  Button,
  CircularProgress,
} from '@material-ui/core';
import {
  TextField,
  Alert,
  Collapse,
  ClickAwayListener,
  Grid,
  Divider,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { makeStyles, Theme } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import SmartToyTwoToneIcon from '@mui/icons-material/SmartToyTwoTone';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import Autocomplete from '@mui/material/Autocomplete';
import { calculateInvestmentReturns, convertCurrency, formatCurrency } from 'src/utils/functions';
import {
  ADR,
  OccupancyRate,
  RevCalReturnObj,
  CalculateRevValues,
  SimulationSnapshot,
  BookingsPerMonth,
  ProfitCalculatorValues,
} from 'src/interfaces/idashboard';
import { infoIconStyle } from '../../Dashboards/Market/styles/style';
import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_GREY,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  exchangeRates,
  investmentCalInputKeys,
} from 'src/const';
import TooltipCard from './TooltipCard';
import InfoTooltip from '../Tooltips/InfoTooltip';
import { SaveROISimulationModal } from 'src/App/Modals';
import { ROISimulationCardPropTypes } from 'src/interfaces/iproptypes';
import { callApi } from 'src/core/callApi';
import { Console } from 'console';
import {
  COLOR_PRIMARY_1,
  SAVE_TO_ANALYSIS_BUTTON,
  SAVE_TO_ANALYSIS_BUTTON_HOVER,
  SIMULATOR_ASSISTANT_BUTTON,
  SIMULATOR_ASSISTANT_BUTTON_HOVER,
} from 'src/colors';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStyles = makeStyles((theme: Theme) => ({
  iconButton: {
    padding: 5,
  },
  inputSubfieldContainer: {
    padding: theme.spacing(2),
    width: 400,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  dropDownToggleBtn: {
    color: COLOR_GREY,
  },
  button: {
    background: 'white',
    borderRadius: 5,
    border: `1px solid ${COLOR_GREY + '44'}`,
    color: `${COLOR_GREY}`,
    padding: '5px 30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    textTransform: 'capitalize',
    '&:hover': {
      color: COLOR_GREY,
    },
    fontSize: 16,
  },
  simulatorAssistantButton: {
    backgroundColor: SIMULATOR_ASSISTANT_BUTTON,
    color: '#ffffff',
    fontSize: 16,
    border: '1px solid',
    borderColor: SIMULATOR_ASSISTANT_BUTTON,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down(600)]: {
      margin: '20px auto !important',
      padding: '10px auto',
    },
    '&:hover': {
      backgroundColor: SIMULATOR_ASSISTANT_BUTTON_HOVER,
      color: '#ffffff',
      borderColor: SIMULATOR_ASSISTANT_BUTTON,
    },
  },
  saveToAnalysisButton: {
    backgroundColor: SAVE_TO_ANALYSIS_BUTTON,
    color: '#ffffff',
    fontSize: 16,
    border: '1px solid',
    borderColor: SAVE_TO_ANALYSIS_BUTTON,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down(600)]: {
      margin: '20px auto !important',
      padding: '10px auto',
    },
    '&:hover': {
      backgroundColor: SAVE_TO_ANALYSIS_BUTTON_HOVER,
      color: '#ffffff',
      borderColor: SAVE_TO_ANALYSIS_BUTTON,
    },
  },
  inputField: {
    marginBottom: 10,
    [theme.breakpoints.down('md')]: {
      marginRight: 10,
    },
  },
  simulatorResultTitle: {
    fontSize: 16,
    // fontWeight: 'bold',
    color: COLOR_GREY,
  },
  simulatorResultValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: COLOR_PRIMARY_DARK,
  },
  calculatorInputs: {
    fontSize: 18,
    fontWeight: 'bold',
    color: COLOR_BLUE_BLACK_LIGHT,
  },
  editDetailsContainer: {
    transition: theme.transitions.create(['max-height'], {
      duration: theme.transitions.duration.standard,
    }),
    maxHeight: 0,
    overflow: 'hidden',
  },
  editDetailsContainerOpen: {
    maxHeight: 1000,
  },
}));

const ROISimulationCard = ({
  investmentCalInputVals,
  setInvestmentCalInputVals,
  isDisabled,
  currency,
  user,
  setSimulateROIPopupOpen,
  setUnlockRegionModalOpen,
  setSubscriptionModalOpen,
  locallySyncSimulatorInputs,
  simulatorUsedOnce,
  address,
  coordinate,
  annualRevenue,
  revenueMonthly,
  nightlyRate,
  occupancyRate,
  bookingsPerMonth,
  filters,
  isPdf,
}) => {
  const classes = useStyles();
  const [hideDetails, setHideDetails] = useState(isPdf ? false : true);
  const [calcInputObj, setCalcInputObj] = useState<CalculateRevValues>({
    annualRevenue: 0,
    downPayment: 0,
    startupCosts: 0,
    annualExpenses: 0,
    propertyPrice: 0,
  });

  const [saveSnapshotModalOpen, setSaveSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotSaved, setSnapshotSaved] = useState<boolean>(false);
  const [snapshotObj, setSnapshotObj] = useState<SimulationSnapshot>({
    propertyPrice: 0,
    downPayment: 0,
    managementFee: 0,
    propertyTax: 0,
    lodgingTax: 0,
    mortgageFees: 0,
    hoaFees: 0,
    maintenance: 0,
    insurance: 0,
    electricity: 0,
    wifi: 0,
    gas: 0,
    other: 0,
    homeFurnishings: 0,
    closingCosts: 0,
    name: '',
    link: '',
    bedrooms: 0,
    bathrooms: 0,
    size: 0,
    note: '',
    address: '',
    coordinates: '',
    nightlyRate: 0,
    occupancyRate: 0,
    bookingsPerMonth: 0,
    annualRevenue: 0,
    monthlyRevenues: 0,
    netProfitBeforeTax: 0,
    grossRentalYield: 0,
    netRentalYield: 0,
    cashOnCashReturn: 0,
    currency: 'USD',
  });
  const defaultLoanPeriod = investmentCalInputVals.loanTerm.toString();
  const [loanPeriod, setLoanPeriod] = React.useState(defaultLoanPeriod);
  const [downPaymentPercentage, setDownPaymentPercentage] = React.useState(20);
  const [downPaymentAmount, setDownPaymentAmount] = React.useState(
    investmentCalInputVals.downPayment
  );
  const [InterestRate, setInterestRate] = React.useState(investmentCalInputVals.interestRate);
  const [loanTaken, setLoanTaken] = React.useState(true);

  const handleLoanPeriodChange = (event, value) => {
    if(value){
      setLoanPeriod(value.year);
    }
  };
  
  const [loanTimePeriods, setLoanTimePeriods] = useState([
    { label: '10 Years', year: 10 },
    { label: '15 Years', year: 15 },
    { label: '30 Years', year: 30 },
  ]);

  const handleOnTimePeriodChange = (event, value) => {
    const customYear = value ? parseInt(value.match(/\d+/)?.[0]) : null;
    if (customYear && customYear > 0) {
      // Check if the entered year already exists in loanTimePeriods
      const yearExists = loanTimePeriods.some(period => period.year == customYear);
      if (!yearExists) {
        // If it doesn't exist, add it to the loanTimePeriods array
        const newPeriods = [...loanTimePeriods, { label: `${customYear} Years`, year: customYear }];
        // Sort newPeriods based on years
        newPeriods.sort((a, b) => a.year - b.year);
        setLoanTimePeriods(newPeriods);
      }
    }
  };

  useEffect(() => {
    let percent = (investmentCalInputVals.downPayment / calcInputObj.propertyPrice) * 100;
    setDownPaymentPercentage(20);
  }, []);

  useEffect(() => {
    let tempCalculation = (calcInputObj.propertyPrice / 100) * downPaymentPercentage;
    setDownPaymentAmount(tempCalculation);
  }, [calcInputObj.propertyPrice]);

  useEffect(() => {
    setInterestRate(investmentCalInputVals.interestRate);
  }, [investmentCalInputVals.interestRate]);

  useEffect(() => {
    setLoanTaken(investmentCalInputVals.takingLoan === 0 ? false : true);
  }, [investmentCalInputVals.takingLoan]);

  useEffect(() => {
    if (loanTaken) {
      let percent = parseFloat(
        ((investmentCalInputVals.downPayment / calcInputObj.propertyPrice) * 100).toFixed(1)
      );

      setDownPaymentPercentage(isNaN(percent) ? 20 : percent);
      setDownPaymentAmount(investmentCalInputVals.downPayment);
    } else {
      setDownPaymentPercentage(100);
      setDownPaymentAmount(calcInputObj.propertyPrice);
      setInvestmentCalInputVals({
        ...investmentCalInputVals,
        ['mortgageFees']: 0,
      });
    }
  }, [loanTaken, investmentCalInputVals.downPayment, calcInputObj.propertyPrice]);

  useEffect(() => {
    let monthlyInterestRate = InterestRate / 100 / 12;
    let principleAmount = calcInputObj.propertyPrice - downPaymentAmount;
    let powerOfCompounding = Math.pow(1 + monthlyInterestRate, parseFloat(loanPeriod) * 12);
    let morgageAmount =
      principleAmount * ((monthlyInterestRate * powerOfCompounding) / (powerOfCompounding - 1));
    // handleValueChange()
    if (morgageAmount > 0 || isNaN(morgageAmount)) {
      setInvestmentCalInputVals({
        ...investmentCalInputVals,
        ['mortgageFees']: morgageAmount.toFixed(2),
      });
    }
  }, [calcInputObj.propertyPrice, downPaymentAmount, InterestRate, loanPeriod]);

  useEffect(() => {
    // make sure all the inputs are numbers
    for (let key in investmentCalInputVals) {
      if (isNaN(investmentCalInputVals[key])) {
        investmentCalInputVals[key] = 0;
      }
    }
    setCalcInputObj({
      ...calcInputObj,
      propertyPrice: investmentCalInputVals.propertyPrice,
      downPayment: investmentCalInputVals.downPayment,
      annualRevenue: annualRevenue,
      annualExpenses: Math.round(
        (investmentCalInputVals.managementFee / 100) * annualRevenue +
          (investmentCalInputVals.lodgingTax / 100) * annualRevenue +
          (investmentCalInputVals.propertyTax / 100) * annualRevenue +
          investmentCalInputVals.mortgageFees * 12 +
          investmentCalInputVals.maintenance +
          investmentCalInputVals.insurance +
          investmentCalInputVals.electricity * 12 +
          investmentCalInputVals.wifi * 12 +
          investmentCalInputVals.gas * 12 +
          investmentCalInputVals.hoaFees +
          investmentCalInputVals.other
      ),
      startupCosts: investmentCalInputVals.homeFurnishings + investmentCalInputVals.closingCosts,
    } as React.SetStateAction<CalculateRevValues>);
  }, [investmentCalInputVals, annualRevenue]);

  useEffect(() => {
    setLoanPeriod(investmentCalInputVals.loanTerm.toString());
  }, [investmentCalInputVals.loanTerm]);

  useEffect(() => {
    setDownPaymentAmount(investmentCalInputVals.downPayment);
  }, [investmentCalInputVals.downPayment]);

  const calcResultObj = calculateInvestmentReturns({
    ...calcInputObj,
  }) as RevCalReturnObj;

  const handleLoanTaken = value => {
    setLoanTaken(value.target.checked);
  };

  const autosaveSimulatorInputsWrapper = () => {
    setInvestmentCalInputVals(values => {
      locallySyncSimulatorInputs && locallySyncSimulatorInputs(values);
      return values;
    });
  };
  const debouncedAutosave = useCallback(_debounce(autosaveSimulatorInputsWrapper, 5000), []);

  const takeSnapshot = async () => {
    setSnapshotSaving(true);
    setSnapshotObj({
      ...snapshotObj,
      ...investmentCalInputVals,
      bedrooms: filters.bedrooms,
      bathrooms: filters.bathrooms,
      address: address ? address : '',
      coordinates: coordinate ? coordinate : '',
      nightlyRate: nightlyRate,
      occupancyRate: occupancyRate,
      bookingsPerMonth: bookingsPerMonth,
      annualRevenue: annualRevenue,
      monthlyRevenues: revenueMonthly,
      netProfitBeforeTax: calcResultObj.netProfitBeforeTax,
      grossRentalYield: calcResultObj.grossRentalYield,
      netRentalYield: calcResultObj.netRentalYield,
      cashOnCashReturn: calcResultObj.cashOnCashReturn,
      currency: currency,
    });
    const workbookCrudUrl =
      'https://vdklywk2td.execute-api.us-east-2.amazonaws.com/default/workbookCRUD';
    let config = {};
    await setSnapshotObj(e => {
      console.log(e);
      config = {
        queryParams: {
          action: 'create',
          ...e,
        },
      };
      return e;
    });
    const response = await callApi(workbookCrudUrl, config, true);
    if (response.message.status === 'created') {
      setSnapshotSaved(true);
    } else {
      setSnapshotSaved(false);
    }
    setSnapshotSaving(false);
  };

  const countDigitsAfterDecimal = (num: number): number => {
    const strNum = num.toString(); // Convert number to string
    const decimalIndex = strNum.indexOf('.'); // Find the index of decimal point
    if (decimalIndex === -1) {
      return 0; // If no decimal point found, return 0
    } else {
      return strNum.length - decimalIndex - 1; // Calculate number of digits after decimal
    }
  };

  const handleValueChange = (key: string, event: any) => {
    let value = event.target.value;
    if (key !== 'interestRate') {
      value = parseFloat(value.replaceAll(',', ''));
    } else {
      if (countDigitsAfterDecimal(value) > 2) {
        value = parseFloat(value.replaceAll(',', '')).toFixed(2);
      }
    }

    if (key === 'downPayment') {
      value > 0 ? setDownPaymentAmount(value) : setDownPaymentAmount(0);
      let percentCalculated = (value / calcInputObj.propertyPrice) * 100;
      if (percentCalculated < 0 || isNaN(percentCalculated)) {
        percentCalculated = 0;
      }
      setDownPaymentPercentage(parseFloat(percentCalculated.toFixed(1)));
    }

    if (key === 'interestRate') {
      let tempInterestRate = value;
      if (tempInterestRate <= 0 || isNaN(tempInterestRate)) tempInterestRate = 0;
      setInterestRate(tempInterestRate);
    }

    if (key === 'downPaymentPercentage') {
      value < 0 || isNaN(value)
        ? setDownPaymentPercentage(0)
        : setDownPaymentPercentage(parseFloat(value.toFixed(1)));

      let downPaymentValue = (calcInputObj.propertyPrice / 100) * parseFloat(value.toFixed(1));

      investmentCalInputVals.downPayment = downPaymentValue;
    }

    if (
      (key === 'managementFee' || key === 'lodgingTax' || key === 'propertyTax') &&
      (value > 100 || value < 0)
    ) {
      window.alert('Please enter a number between 0 and 100');
      setInvestmentCalInputVals({
        ...investmentCalInputVals,
        [key]: investmentCalInputVals[key],
      });
    } else if (value < 0) {
      window.alert('Please enter a number greater than 0');
      setInvestmentCalInputVals({
        ...investmentCalInputVals,
        [key]: investmentCalInputVals[key],
      });
    } else {
      setSnapshotSaved(false);
      setInvestmentCalInputVals({
        ...investmentCalInputVals,
        [key]: value,
      });
    }
  };

  const label = { inputProps: { 'aria-label': 'Switch demo' } };

  //to make tooltip responsive both desktop and mobile
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  // console.log(">>>>>>>>>>>>>investmentCalInputKeys",investmentCalInputKeys)
  return (
    <>
      <Grid container spacing={2} id="roiCalculator">
        <Grid
          item
          style={{ width: '95%' }}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography>
            {' '}
            Forecast your Income
            <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
              <InfoTooltip
                enterTouchDelay={0}
                leaveTouchDelay={10000}
                open={tooltipOpen}
                onOpen={() => setTooltipOpen(true)}
                onClose={() => setTooltipOpen(false)}
                title={
                  <TooltipCard
                    title="ROI Simulation"
                    content="This simulation uses the past 12 months' average nightly rate and occupancy rate from the listings that you are seeing in the map view. This simulation assumes inflation of expenses and nightly rates (1.5%/year), appreciation of property price (1.5%/year), and 20% profit tax."
                    // link="https://support.airbtics.com/"
                  />
                }
              >
                <IconButton
                  className={classes.iconButton}
                  onClick={() => setTooltipOpen(prev => !prev)}
                >
                  <InfoOutlinedIcon style={infoIconStyle} />{' '}
                </IconButton>
              </InfoTooltip>
            </ClickAwayListener>
          </Typography>

          {/* <Button
            className={classes.simulatorAssistantButton}
            size="small"
            variant="outlined"
            color="primary"
            onClick={e => {
              mixpanel.track('openSimulatorAssistantButttonClicked');
              if (!isDisabled) {
                setSimulateROIPopupOpen && setSimulateROIPopupOpen(true);
              } else {
                if (user && user.estimatorCredits > 0) {
                  setUnlockRegionModalOpen && setUnlockRegionModalOpen(true);
                } else {
                  setSubscriptionModalOpen && setSubscriptionModalOpen(true);
                }
              }
            }}
          >
            {!simulatorUsedOnce && 'quick setup'}
            <SmartToyTwoToneIcon sx={{ paddingLeft: 1 }} />
          </Button> */}
        </Grid>

        {isDisabled && (
          <Grid item style={{ width: '100%' }}>
            <Alert severity="error" style={{ marginTop: 10 }}>
              You need to unlock this report to use this simulator! (We have filled sample revenue
              data, it is not the market average. Subscribe now to see the full data.)
            </Alert>
          </Grid>
        )}

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          style={{ width: '100%' }}
        >
          {Object.keys(investmentCalInputKeys).map((field: string) => {
            return (
              <>
                {investmentCalInputKeys[field].input ? (
                  <Grid item key={field} xs={6} className={classes.inputField}>
                    <InfoTooltip
                      title={
                        <TooltipCard
                          title={investmentCalInputKeys[field].label}
                          content={investmentCalInputKeys[field].tooltip}
                        />
                      }
                      arrow
                    >
                      <TextField
                        id={investmentCalInputKeys[field].label}
                        label={investmentCalInputKeys[field].label}
                        variant="outlined"
                        size="small"
                        fullWidth
                        defaultValue={investmentCalInputVals[investmentCalInputKeys[field].key]}
                        value={
                          investmentCalInputKeys[field].unit === '%'
                            ? investmentCalInputVals[investmentCalInputKeys[field].key].toString()
                            : formatCurrency(
                                investmentCalInputVals[investmentCalInputKeys[field].key].toString()
                              )
                        }
                        onChange={event => {
                          handleValueChange(investmentCalInputKeys[field].key, event);
                          debouncedAutosave();
                        }}
                        type={investmentCalInputKeys[field].unit === '%' ? 'number' : 'tel'}
                        error={!investmentCalInputVals[investmentCalInputKeys[field].key]}
                        InputProps={
                          investmentCalInputKeys[field].unit === '$'
                            ? {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {exchangeRates[currency].symbol}
                                  </InputAdornment>
                                ),
                              }
                            : {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {investmentCalInputKeys[field].unit}
                                  </InputAdornment>
                                ),
                              }
                        }
                      />
                    </InfoTooltip>
                  </Grid>
                ) : (
                  <Grid item key={field} xs={6} className={classes.inputField}>
                    <InfoTooltip
                      title={
                        <TooltipCard
                          title={investmentCalInputKeys[field].label}
                          content={investmentCalInputKeys[field].tooltip}
                        />
                      }
                      arrow
                    >
                      <div>
                        <Typography className={classes.simulatorResultTitle}>
                          {investmentCalInputKeys[field].label}
                        </Typography>
                        <Typography className={classes.calculatorInputs}>
                          {exchangeRates[currency].symbol}
                          {field === 'annualRevenue'
                            ? formatCurrency(annualRevenue.toString())
                            : formatCurrency(
                                calcInputObj[investmentCalInputKeys[field].key].toString()
                              )}
                        </Typography>
                      </div>
                    </InfoTooltip>
                  </Grid>
                )}
              </>
            );
          })}
        </Grid>

        <Divider style={{ width: '100%', marginTop: 20, marginBottom: 20 }} />

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          style={{ width: '100%' }}
        >
          <Grid container item xs={6} gap={0}>
            <Grid item xs={8}>
              <Typography style={{ fontWeight: '500' }}> Are you taking a loan?</Typography>
            </Grid>
            <Grid item xs={3}>
              <Switch {...label} checked={loanTaken} onChange={handleLoanTaken} />
            </Grid>
          </Grid>

          {/* Loan Term */}
          <Grid item xs={6} sx={{ padding: '0px !important' }}>
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              options={loanTimePeriods}
              sx={{
                m: 1,
                minWidth: 150,
                '& .MuiOutlinedInput-root':{
                height:'45px !important',
                },
                '& .MuiOutlinedInput-input':{
                padding:'0px !important',
                }
              }}
              disabled={!loanTaken}
              renderInput={params => <TextField {...params} label="Years" />}
              defaultValue={{ label: '30 Years', year: 30 }}
              onInputChange={handleOnTimePeriodChange}
              onChange={(event,value) => {
                handleLoanPeriodChange(event, value);
                debouncedAutosave();
              }}
            />
          </Grid>

          {/* Down Payment */}
          <Grid container item xs={6} className={classes.inputField}>
            <Grid item xs={12}>
              <TextField
                id="Down Payment"
                label="Down Payment"
                variant="outlined"
                size="small"
                defaultValue={downPaymentAmount}
                value={formatCurrency(downPaymentAmount.toString())}
                onChange={event => {
                  handleValueChange('downPayment', event);
                  debouncedAutosave();
                }}
                type="tel"
                disabled={!loanTaken}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {exchangeRates[currency].symbol}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* <Grid item xs={3}>
              <TextField
                id="downPayment Percentage"
                label="%"
                variant="outlined"
                size="small"
                defaultValue={downPaymentPercentage}
                value={downPaymentPercentage}
                onChange={event => {
                  handleValueChange('downPaymentPercentage', event);
                  debouncedAutosave();
                }}
                disabled={!loanTaken}
                sx={{ '& .MuiOutlinedInput-input': { padding: '8.5px 5px' } }}
              />
            </Grid> */}
          </Grid>

          {/* Interest Rate */}
          <Grid item xs={6} className={classes.inputField}>
            <TextField
              id="Interest Rate"
              label="Interest Rate"
              variant="outlined"
              size="small"
              fullWidth
              defaultValue={InterestRate}
              value={InterestRate}
              onChange={event => {
                console.log('>>>>event', event.target.value);
                handleValueChange('interestRate', event);
                debouncedAutosave();
              }}
              type="tel"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              disabled={!loanTaken}
            />
          </Grid>
          <Typography
            style={{
              paddingLeft: '16px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              fontStyle: 'italic',
            }}
          >
            <InfoOutlinedIcon style={{ ...infoIconStyle, color: '#000' }} /> Your down payment of{' '}
            {exchangeRates[currency].symbol + formatCurrency(downPaymentAmount.toString())} is{' '}
            {downPaymentPercentage}% of{' '}
            {exchangeRates[currency].symbol +
              formatCurrency(investmentCalInputVals.propertyPrice.toString())}
          </Typography>
        </Grid>

        <Divider style={{ width: '100%', marginTop: 20, marginBottom: 20 }} />

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          spacing={4}
          style={{ width: '100%' }}
          xs={12}
        >
          <Grid container item xs={12} spacing={2} justifyContent="space-between">
            <Grid item xs={6}>
              <InfoTooltip
                title={
                  <TooltipCard
                    title="Gross Rental Yield: (Annual Gross Rental Income / Property Price) * 100"
                    content="Gross rental yield is the annual gross income generated by an asset, divided by it's price."
                  />
                }
                arrow
              >
                <div>
                  <Typography className={classes.simulatorResultTitle}>
                    Gross Rental Yield
                  </Typography>
                  <Typography className={classes.simulatorResultValue}>
                    {calcResultObj['grossRentalYield']}%
                  </Typography>
                </div>
              </InfoTooltip>
            </Grid>

            <Grid item xs={6}>
              <InfoTooltip
                title={
                  <TooltipCard
                    title="Net Rental Yield: (Annual Cash Flow / Property Price) * 100"
                    content="Net rental yield is the new annual cash flow generated by an asset, divided by it's price."
                  />
                }
                arrow
              >
                <div>
                  <Typography className={classes.simulatorResultTitle}>Net Rental Yield</Typography>
                  <Typography className={classes.simulatorResultValue}>
                    {calcResultObj['netRentalYield']}%
                  </Typography>
                </div>
              </InfoTooltip>
            </Grid>

            <Grid item xs={6}>
              <InfoTooltip
                title={
                  <TooltipCard content="The yearly profit is based on the historical performance of the listings you see on the map combined with the information you provided above. To be specific, it uses the occupancy rate, the median nightly rate and the revenue values that you can see in the above charts." />
                }
                arrow
              >
                <div>
                  <Typography className={classes.simulatorResultTitle}>
                    Net Profit Before Tax:{' '}
                  </Typography>
                  <Typography className={classes.simulatorResultValue}>
                    {exchangeRates[currency].symbol}
                    {formatCurrency(calcResultObj['netProfitBeforeTax'].toString())}
                  </Typography>
                </div>
              </InfoTooltip>
            </Grid>

            <Grid item xs={6}>
              <InfoTooltip
                title={
                  <TooltipCard content="Cash on Cash Return is a rate of return that calculates the annual cash flow on the cash invested in a property." />
                }
                arrow
              >
                <div>
                  <Typography className={classes.simulatorResultTitle}>
                    Cash on Cash Return:{' '}
                  </Typography>
                  <Typography className={classes.simulatorResultValue}>
                    {calcResultObj['cashOnCashReturn']}%
                  </Typography>
                </div>
              </InfoTooltip>
            </Grid>

            <Grid item xs={12}>
              <Button
                className={classes.button}
                onClick={() => setHideDetails(!hideDetails)}
                style={{ width: '100%' }}
              >
                {hideDetails ? 'Advanced' : 'less'} Options{' '}
                {hideDetails ? (
                  <ArrowDropDownIcon
                    className={classes.dropDownToggleBtn}
                    fontSize="large"
                    style={{ marginRight: '-15px' }}
                  />
                ) : (
                  <ArrowDropUpIcon
                    className={classes.dropDownToggleBtn}
                    fontSize="large"
                    style={{ marginRight: '-15px' }}
                  />
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          className={clsx(classes.editDetailsContainer, {
            [classes.editDetailsContainerOpen]: !hideDetails,
          })}
          sx={{ margin: 0, padding: 0, marginTop: '-20px', paddingLeft: '0px !important' }}
        >
          <Grid container item xs={12}>
            <Typography
              variant="h5"
              style={{
                marginBottom: 10,
                marginTop: -5,
                color: COLOR_PRIMARY,
                fontWeight: 600,
                fontSize: '20px',
              }}
              className={classes.inputSubfieldContainer}
            >
              Advanced Settings
            </Typography>
          </Grid>
          <Grid
            container
            item
            xs={12}
            direction="column"
            className={classes.inputSubfieldContainer}
          >
            <Typography variant="h6" style={{ marginBottom: 20, marginTop: 0, fontSize: '16px' }}>
              Operating Expenses
            </Typography>

            <Grid item container spacing={2}>
              {investmentCalInputKeys.annualExpenses.subfields &&
                investmentCalInputKeys.annualExpenses.subfields.map((subfield, subfieldIndex) => {
                  return (
                    <Grid item xs={6} key={subfieldIndex}>
                      <TextField
                        id={subfield.label}
                        label={subfield.label}
                        variant="outlined"
                        size="small"
                        fullWidth
                        defaultValue={investmentCalInputVals[subfield.key]}
                        value={
                          subfield.unit === '%'
                            ? investmentCalInputVals[subfield.key].toString()
                            : formatCurrency(investmentCalInputVals[subfield.key].toString())
                        }
                        onChange={event => {
                          handleValueChange(subfield.key, event);
                          debouncedAutosave();
                        }}
                        type={subfield.unit === '%' ? 'number' : 'tel'}
                        error={!investmentCalInputVals[subfield.key]}
                        InputProps={
                          subfield.unit === '$'
                            ? {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {exchangeRates[currency].symbol}
                                  </InputAdornment>
                                ),
                              }
                            : {
                                endAdornment: (
                                  <InputAdornment position="end">{subfield.unit}</InputAdornment>
                                ),
                              }
                        }
                      />
                    </Grid>
                  );
                })}
            </Grid>
          </Grid>

          <Grid item xs={12} container direction="column">
            <Grid container item direction="column" className={classes.inputSubfieldContainer}>
              <Typography variant="h6" style={{ marginBottom: 20, marginTop: 0, fontSize: '16px' }}>
                Startup Costs
              </Typography>

              <Grid item container spacing={2}>
                {investmentCalInputKeys.startupCosts.subfields &&
                  investmentCalInputKeys.startupCosts.subfields.map((subfield, subfieldIndex) => (
                    <Grid item xs={6} key={subfieldIndex}>
                      <TextField
                        id={subfield.label}
                        label={subfield.label}
                        variant="outlined"
                        size="small"
                        fullWidth
                        defaultValue={investmentCalInputVals[subfield.key]}
                        value={formatCurrency(investmentCalInputVals[subfield.key].toString())}
                        onChange={event => {
                          handleValueChange(subfield.key, event);
                          debouncedAutosave();
                        }}
                        type="tel"
                        error={!investmentCalInputVals[subfield.key]}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {exchangeRates[currency].symbol}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="flex-end"
          sx={{ margin: 0, marginRight: '15px', padding: 0, marginTop: '-10px' }}
        >
          {snapshotSaving ? (
            <CircularProgress size={20} />
          ) : snapshotSaved ? (
            <BookmarkAddedIcon style={{ color: COLOR_PRIMARY }} />
          ) : (
            <InfoTooltip
              title={<TooltipCard content="Save the current simulation snapshot in MyAnalysis" />}
              arrow
            >
              {/* <IconButton onClick={() => setSaveSnapshotModalOpen(true)}>
                <BookmarkAddIcon style={{ color: COLOR_PRIMARY }} />
              </IconButton> */}
              <Button
                className={classes.saveToAnalysisButton}
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => setSaveSnapshotModalOpen(true)}
              >
                Save to My Analysis
              </Button>
            </InfoTooltip>
          )}
        </Grid>
      </Grid>

      {saveSnapshotModalOpen && (
        <SaveROISimulationModal
          saveSnapshotModalOpen={saveSnapshotModalOpen}
          setSaveSnapshotModalOpen={setSaveSnapshotModalOpen}
          takeSnapshot={takeSnapshot}
          snapshotObj={snapshotObj}
          setSnapshotObj={setSnapshotObj}
        />
      )}
    </>
  );
};

export default ROISimulationCard;
